import _ from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SmartDateTimeFormatterContainer} from '../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {
  FutureTimeSourcesEnum,
  OnFutureTimeValueSelectOptions,
} from '../../../../srcCommon/models/futureTimeDropdownTypes';
import {buildAllFutureTimePresets, RenderablePreset} from '../../../helpers/models/snoozePresetHelpers';
import {CustomSnoozeModel} from '../../../models/api/customSnoozeModel';
import {FutureTimeValuesEnum} from '../../../models/api/futureTimeValueModel';
import {ReminderPresets} from '../../../models/api/preferencesModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {DropdownItemChevron} from '../dropdowns/dropdownItemChevron';
import {DropdownItemSecondaryContent} from '../dropdowns/dropdownItemSecondaryContent';
import {DropdownItemSeparator} from '../dropdowns/dropdownItemSeparator';

/*
 * Props.
 */

export interface FutureTimeDropdownPresetsListProps extends DropdownProps {
  values: ReadonlyArray<FutureTimeValuesEnum>;
  reminderPresets: ReminderPresets | null;
  customSnoozePreset?: ReminderModel;
  customSnoozes: ReadonlyArray<CustomSnoozeModel> | null;
  onValueSelect: HandlerOf<OnFutureTimeValueSelectOptions>;
  showCustom: HandlerOf<FrontEvent>;
}

/*
 * Style.
 */

const StyledDropdownList = styled(DropdownList)`
  min-width: 320px;
  max-height: 323px; /* 10.5 rows */
`;

interface ColorStyleProps {
  $color: string;
}
const StyledColorDiv = styled.div<ColorStyleProps>`
  color: ${(p) => p.$color};
`;

const StyledPresetSecondaryDiv = styled(StyledColorDiv)`
  text-align: right;
`;

/*
 * Component.
 */

export const FutureTimeDropdownPresetsList: FC<FutureTimeDropdownPresetsListProps> = (props) => {
  const theme = useUserTheme();
  const {reminderPresets, customSnoozes, values} = props;

  const presets = useMemo(
    () => buildAllFutureTimePresets(reminderPresets, customSnoozes, values),
    [reminderPresets, customSnoozes, values],
  );
  const renderedPresets = renderPresets(props, presets);

  return (
    <StyledDropdownList {...props}>
      {renderCustomPreset(props)}
      {renderedPresets}
      <DropdownItemSeparator customMargin={8} />
      <DropdownItem key="custom" onSelect={props.showCustom}>
        <DropdownItemContent>
          <StyledColorDiv $color={theme.greys.shade80}>
            <Trans id="futuretimepresets-custom">Day &amp; Time</Trans>
          </StyledColorDiv>
        </DropdownItemContent>
        <DropdownItemChevron />
      </DropdownItem>
    </StyledDropdownList>
  );
};

function renderCustomPreset(props: FutureTimeDropdownPresetsListProps) {
  const {customSnoozePreset} = props;

  if (!customSnoozePreset) {
    return null;
  }

  const onPresetSelect = (event: FrontEvent) => {
    event.preventDefault();
    props.onValueSelect({datetime: customSnoozePreset.remindAt, source: FutureTimeSourcesEnum.SNOOZE_AGAIN});
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <DropdownItem key="snooze-dropdown-last-snooze" onSelect={onPresetSelect}>
          <DropdownItemContent>
            <StyledColorDiv $color={theme.greys.shade80}>
              <Trans id="reminder-re-snooze">Last snooze</Trans>
            </StyledColorDiv>
          </DropdownItemContent>
          <DropdownItemSecondaryContent>
            <StyledPresetSecondaryDiv $color={theme.greys.shade60}>
              <SmartDateTimeFormatterContainer value={customSnoozePreset.remindAt} />
            </StyledPresetSecondaryDiv>
          </DropdownItemSecondaryContent>
        </DropdownItem>
      )}
    </UserThemeConsumer>
  );
}

function renderPresets(props: FutureTimeDropdownPresetsListProps, presets: ReadonlyArray<RenderablePreset>) {
  const onPresetSelect = (event: FrontEvent, preset: RenderablePreset) => {
    event.preventDefault();
    const presetDate = preset.date.toMillis();
    props.onValueSelect({datetime: presetDate, source: FutureTimeSourcesEnum.PRESET});
  };

  return _(presets)
    .sortBy((p) => p.date)
    .map((preset) => (
      <UserThemeConsumer>
        {(theme) => (
          <DropdownItem key={preset.key} onSelect={(e) => onPresetSelect(e, preset)}>
            <DropdownItemContent>
              <StyledColorDiv $color={theme.greys.shade80}>{preset.title}</StyledColorDiv>
            </DropdownItemContent>
            <DropdownItemSecondaryContent>
              <StyledPresetSecondaryDiv $color={theme.greys.shade60}>
                <SmartDateTimeFormatterContainer value={preset.date.toMillis()} />
              </StyledPresetSecondaryDiv>
            </DropdownItemSecondaryContent>
          </DropdownItem>
        )}
      </UserThemeConsumer>
    ))
    .value();
}
